import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@hooks/useStores';
import { Loader } from '@components/common/ui';
import { GlobeIcon, LockIcon } from '@components/common/ui/icons';
import { API_URL } from '@http';
import $api from '@http';
import * as styles from '@pages/SharePage.module.css';

interface ShareLinkInfo {
    noteId: string;
    title: string;
    permission: 'read' | 'edit';
    expiresAt: string | null;
}

type PageState = 'loading' | 'ready' | 'error' | 'expired';

export const SharePage: React.FC = observer(() => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const authStore = useAuthStore();

    const [state, setState] = useState<PageState>('loading');
    const [info, setInfo] = useState<ShareLinkInfo | null>(null);
    const [errorMsg, setErrorMsg] = useState('');

    const isGuest = !authStore.isAuth;

    useEffect(() => {
        if (!token) {
            setState('error');
            setErrorMsg(t('share.page.invalidLink'));
            return;
        }

        const load = async () => {
            try {
                const res = await fetch(`${API_URL}/share-link/${token}/info`);
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    if (res.status === 400) {
                        setState('expired');
                    } else {
                        setState('error');
                        setErrorMsg(body.message || t('share.page.linkNotFound'));
                    }
                    return;
                }
                const data = await res.json();
                setInfo(data);
                setState('ready');
            } catch {
                setState('error');
                setErrorMsg(t('share.page.loadFailed'));
            }
        };

        load();
    }, [token, t]);

    const handleOpen = async () => {
        if (!info || !token) return;

        if (isGuest) {
            navigate(`/note/${info.noteId}?shareToken=${token}`);
            return;
        }

        $api.post('/share-link/connect', { token }).catch(() => {});
        navigate(`/note/${info.noteId}?shareToken=${token}`);
    };

    const handleLoginToEdit = () => {
        navigate(`/login?returnTo=/share/${token}`);
    };

    const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

    if (state === 'loading') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <Loader variant="spinner" size="lg" text={t('share.page.loading')} />
                </div>
            </div>
        );
    }

    if (state === 'expired') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.iconWrap} data-variant="warn">
                        <LockIcon className={styles.icon} />
                    </div>
                    <h1 className={styles.title}>{t('share.page.expiredTitle')}</h1>
                    <p className={styles.desc}>{t('share.page.expiredDescription')}</p>
                    <button className={styles.btnPrimary} onClick={() => navigate('/')}>
                        {t('activation.backHome')}
                    </button>
                </div>
            </div>
        );
    }

    if (state === 'error') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.iconWrap} data-variant="danger">
                        <LockIcon className={styles.icon} />
                    </div>
                    <h1 className={styles.title}>{t('share.page.invalidLink')}</h1>
                    <p className={styles.desc}>{errorMsg || t('share.page.invalidDescription')}</p>
                    <button className={styles.btnPrimary} onClick={() => navigate('/')}>
                        {t('activation.backHome')}
                    </button>
                </div>
            </div>
        );
    }

    if (!info) return null;

    const canGuestRead = isGuest && info.permission === 'read';
    const guestNeedsLogin = isGuest && info.permission === 'edit';

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.iconWrap} data-variant="accent">
                    <GlobeIcon className={styles.icon} />
                </div>

                <h1 className={styles.title}>{info.title || t('landing.untitled')}</h1>

                <div className={styles.meta}>
                    <span className={styles.permBadge} data-perm={info.permission}>
                        {info.permission === 'edit'
                            ? t('share.permission.editFull')
                            : t('share.permission.viewOnly')}
                    </span>
                    {info.expiresAt && (
                        <span className={styles.expiryBadge}>
                            {t('share.page.validUntil', {
                                date: new Date(info.expiresAt).toLocaleDateString(dateLocale),
                            })}
                        </span>
                    )}
                </div>

                {guestNeedsLogin ? (
                    <>
                        <p className={styles.desc}>{t('share.page.guestEditHint')}</p>
                        <div className={styles.actions}>
                            <button className={styles.btnPrimary} onClick={handleOpen}>
                                {t('share.page.openReadOnly')}
                            </button>
                            <button className={styles.btnSecondary} onClick={handleLoginToEdit}>
                                {t('share.page.loginToEdit')}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className={styles.desc}>
                            {canGuestRead
                                ? t('share.page.guestReadHint')
                                : t('share.page.openHint')}
                        </p>
                        <div className={styles.actions}>
                            <button
                                className={styles.btnPrimary}
                                onClick={handleOpen}
                            >
                                {canGuestRead
                                    ? t('share.page.openReadOnly')
                                    : t('share.page.openNote')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});
